import type { Intervention } from '@seen/core/models/care-plans/intervention'
import { Button, cn } from '@seen/ui'
import { IconChevronRight, IconDotsVertical } from '@tabler/icons-react'
import { forwardRef, type ReactNode } from 'react'
import { InterventionActions } from '#src/components/care-plan/interventions/InterventionActions.tsx'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import type { Field } from '#src/components/core/form/FormField.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { IconInterventionStatus } from '#src/components/icons/IconInterventionStatus.tsx'
import { IconUserPlaceholder } from '#src/components/icons/IconUserPlaceholder.tsx'
import { InterventionStatusCombobox } from '#src/components/InterventionStatusCombobox.tsx'
import { PractitionerNameplate } from '#src/components/nameplates/PractitionerNameplate.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { RichText } from '#src/components/RichText.tsx'
import { useOwnerComboboxOptions } from '#src/components/UseOwnerComboboxOptions.tsx'
import { useInterventionIsEditable } from '#src/hooks/useInterventionIsEditable.ts'
import { useNavigateFromClickEvent } from '#src/hooks/useNavigateFromClickEvent.ts'

export interface InterventionListProps {
  interventions: Intervention[]
}

export function InterventionList({
  interventions,
}: InterventionListProps): ReactNode {
  return (
    <div>
      {interventions.map((intervention) => (
        <InterventionRow
          key={intervention.id}
          intervention={intervention}
          interventions={interventions}
        />
      ))}
    </div>
  )
}

function InterventionRow({
  intervention,
  interventions,
}: {
  intervention: Intervention
  interventions: Intervention[]
}): ReactNode {
  const navigateFromClickEvent = useNavigateFromClickEvent()
  const isEditable = useInterventionIsEditable(intervention)

  return (
    <div
      onClick={(e) => {
        navigateFromClickEvent(`interventions/${intervention.id}`, e)
      }}
      className={cn(
        'flex min-h-10 cursor-default items-start gap-2 px-pagex-default hover:bg-accent lg:px-pagex-lg',
        intervention.isInactive && 'text-foreground-secondary'
      )}
    >
      <div
        onClick={(e) => {
          e.stopPropagation()
        }}
        className="self-stretch"
      >
        <ResourceFieldForm
          instance={intervention}
          valueFn={(i) => i.status}
          patchFn={(i, value) => {
            i.status = value
          }}
          className="h-full"
          render={({ field: { value, onChange, ...rest } }) => (
            <InterventionStatusCombobox
              onSelect={onChange}
              selected={value}
              disabled={!isEditable}
            >
              <Button
                variant="unstyled"
                className="flex h-full items-start p-0 hover:brightness-hover-icon data-[state=open]:brightness-hover-icon"
                {...rest}
              >
                <div className="py-3">
                  <IconInterventionStatus
                    status={value}
                    className={cn(
                      intervention.isInactive && 'text-foreground-secondary'
                    )}
                  />
                </div>
              </Button>
            </InterventionStatusCombobox>
          )}
        />
      </div>
      <div
        className={cn(
          'flex flex-1 self-stretch py-2.5 text-sm',
          intervention.isInactive && 'text-foreground-secondary'
        )}
      >
        {isEditable ? (
          <ResourceFieldForm
            instance={intervention}
            valueFn={(valueIntervention) => valueIntervention.description}
            patchFn={(patchIntervention, value) => {
              patchIntervention.description = value
            }}
            debounceTyping
            render={({ field }) => (
              <RichTextEditor
                placeholder="Add intervention..."
                variant="unstyled"
                contentClassName="cursor-text"
                {...field}
              />
            )}
          />
        ) : (
          <RichText value={intervention.description} />
        )}
      </div>
      <div onClick={(e) => e.stopPropagation()} className="self-stretch">
        <ResourceFieldForm
          instance={intervention}
          valueFn={(intervention) => intervention.author?.reference ?? null}
          patchFn={(intervention, value) => {
            intervention.author = value ? { reference: value } : undefined
          }}
          className="h-full"
          render={({ field }) => (
            <OwnerNameplateCombobox {...field} disabled={!isEditable} />
          )}
        />
      </div>
      <InterventionActions
        intervention={intervention}
        interventions={interventions}
      >
        <Button
          variant="unstyled"
          size="unstyled"
          className="group flex items-start self-stretch py-2"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="py-1 group-hover:backdrop-brightness-hover group-data-[state=open]:backdrop-brightness-hover">
            <IconDotsVertical className="size-4" />
          </div>
        </Button>
      </InterventionActions>
      <div className="flex py-3">
        <IconChevronRight className="size-4" />
      </div>
    </div>
  )
}

const OwnerNameplateCombobox = forwardRef<
  HTMLButtonElement,
  Field<string | null> & { disabled?: boolean }
>(({ value, onChange, disabled, ...fieldRest }, ref) => {
  const options = useOwnerComboboxOptions(value)
  return (
    <Combobox options={options} onSelect={onChange} disabled={disabled}>
      <Button
        variant="unstyled"
        className="flex h-full items-start px-0 py-2.5 hover:brightness-hover-icon data-[state=open]:brightness-hover-icon"
        ref={ref}
        {...fieldRest}
      >
        {value && (
          <PractitionerNameplate
            practitioner={value}
            className="flex-row-reverse gap-1.25 text-xs"
          />
        )}
        {!value && (
          <IconUserPlaceholder className="text-foreground-secondary" />
        )}
      </Button>
    </Combobox>
  )
})
OwnerNameplateCombobox.displayName = 'OwnerNameplateCombobox'
